import React, { useState, useEffect, FC } from "react";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Badge,
  Col,
  Row,
  Modal,
  ModalBody,
} from "reactstrap";
import { Drawer } from "antd";
import { Icon, Button, TooltipComponent } from "../../../components/Component";
import Swal from "sweetalert2";
import gateEntryApis from "../../../api/master/gateEntry";
import sequenceApis from "../../../api/master/sequence";
import { useSelector } from "react-redux";
import TransactionFlowDefaultTemplate from "../../others/transactionFlowDefaultTemplate";
import dayjs from "dayjs";
import GateEntryForm from "./GateEntryForm";
import FormatDate from "../DateFormater";
import { normalizeStatus } from "../../../utils/Utils";

const GateEntry: FC = () => {
  const [refreshToggle, setRefreshToggle] = useState(false);
  const { data: accessibility } = useSelector(
    (state: any) => state.accessibility
  );
  const [allowedActions, setAllowedActions] = useState<any>({
    add: 0,
    view: 0,
    edit: 0,
    approve: 0,
    reject: 0,
    cancel: 0,
  });

  // Drawer states
  const [isOpen, setIsOpen] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [detail, setDetail] = useState<any>({});
  const [isSequenceSet, setIsSequenceSet] = useState(false);
  const [formAction, setFormAction] = useState<string | null>(null);

  const [popUpContent, setPopUpContent] = useState<JSX.Element | null>(null);

  useEffect(() => {
    if (accessibility) {
      try {
        const { add, view, edit, approve, reject, cancel } =
          accessibility["Master"];
        setAllowedActions({ add, view, edit, approve, reject, cancel });
      } catch (error) {
        console.log(error);
      }
    }
  }, [accessibility]);

  // Check if sequence is set for Gate Entry
  useEffect(() => {
    fetchSequenceAccess();
  }, []);

  const fetchSequenceAccess = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      // Check if sequence exists with:
      // 1. moduleName === "Gate Entry"
      // 2. moduleId === 179
      // 3. status === "Active" (if "Inactive", Gate Entry ID field will be shown)
      // 4. endDate is in the future
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "Gate Entry" &&
          item.moduleId === 179 &&
          item.status === "Active" &&
          new Date(item.endDate) > today
      );
      setIsSequenceSet(_sequenceAccess && _sequenceAccess.length > 0);
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
      setIsSequenceSet(false);
    }
  };

  const handleStatusChange = async (
    gateEntryId: any,
    action: "activate" | "inactivate"
  ) => {
    const actionText = action === "activate" ? "Activate" : "InActivate";

    Swal.fire({
      title: `Are you sure?`,
      text: `You want to ${actionText} this gate entry!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText} it!`,
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response =
            action === "activate"
              ? await gateEntryApis.activateGateEntriesByIds([gateEntryId])
              : await gateEntryApis.inactivateGateEntriesByIds([gateEntryId]);

          if (response.status) {
            await Swal.fire(
              "Success!",
              `Gate entry has been ${
                action === "activate" ? "Activated" : "Inactivated"
              }.`,
              "success"
            );
            refreshData();
          } else {
            await Swal.fire("Error", response.message, "error");
          }
        } catch (error: any) {
          await Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  const handleDelete = (gateEntryId: any) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deleteGateEntryById(gateEntryId);
      }
    });
  };

  const deleteGateEntryById = async (id: any) => {
    try {
      const { status, message } = await gateEntryApis.deleteGateEntriesByIds([id]);
      if (status) {
        await Swal.fire("Deleted!", message || "Gate entry has been deleted", "success");
        refreshData();
      } else {
        await Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      await Swal.fire("Error", error.message, "error");
    }
  };

  const refreshData = () => {
    setRefreshToggle((prev) => !prev);
  };

  const loadDetail = async (gateEntryId: any) => {
    try {
      const response = await gateEntryApis.fetchGateEntryById(gateEntryId);
      if (response && response.data) {
        // Update detail with fetched data, preserving existing data if API fails
        setDetail((prevDetail: any) => ({
          ...prevDetail,
          ...response.data,
        }));
      } else {
        console.error("Failed to fetch gate entry details", response);
        // Don't overwrite existing data, just log the error
      }
    } catch (error: any) {
      console.error("Error fetching gate entry details:", error);
      // Try to extract data from error response if available
      if (error?.response?.data) {
        setDetail((prevDetail: any) => ({
          ...prevDetail,
          ...error.response.data,
        }));
      }
      // Don't overwrite existing data on error
    }
  };

  const handleModelPopUp = (id?: any, type?: string) => {
    setFormAction(type || null);
    const content = (
      <GateEntryForm
        formAction={type}
        id={id}
        toggleModelPopUp={() => {
          setIsOpen(false);
        }}
        refreshData={refreshData}
        isSequenceSet={isSequenceSet}
      />
    );
    setPopUpContent(content);
    setIsOpen(true);
  };

  // Define select filters for the template
  const selectFilters = [
    {
      title: "Status",
      placeholder: "Select Status",
      options: [
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "Inactive" },
      ],
      onChange: (value: any) => {
        console.log("Status selected:", value);
      },
    },
  ];

  const columns = [
    {
      title: "Gate Entry Id",
      dataIndex: "gateEntrySequenceId",
      key: "gateEntrySequenceId",
      width: 150,
      render: (text: any) => text || "N/A",
    },
    {
      title: "Gate Entry Number",
      dataIndex: "gateEntryNumber",
      key: "gateEntryNumber",
      width: 150,
      render: (text: any) => text || "N/A",
    },
    {
      title: "Site Name",
      dataIndex: "siteName",
      key: "siteName",
      width: 200,
      render: (text: any) => text || "N/A",
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (text: any) => <span>{dayjs(text).format("DD-MMM-YYYY")}</span>,
    },
    {
      title: "Created By",
      dataIndex: "createdByName",
      key: "createdByName",
      width: 150,
      render: (text: any) => text || "N/A",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (text: any, _: any, index: number) => {
        if (!text || text === "N/A" || text.length <= 25) return text || "N/A";

        return (
          <div className="d-flex align-items-center">
            <span id={`desc-tooltip-${index}`}>
              {text.substring(0, 25)}...
            </span>
            <TooltipComponent
              iconClass="info-circle text-info ms-1"
              icon="info"
              id={`desc-tooltip-${index}`}
              direction="top"
              text={text}
              containerClassName=""
            />
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (text: any) => {
        const normalizedStatus = normalizeStatus(text);
        return (
          <span
            className={`tb-status text-${
              normalizedStatus === "Active" ? "success" : "danger"
            }`}
          >
            {normalizedStatus}
          </span>
        );
      },
    },
    {
      title: "Details",
      dataIndex: "action",
      key: "action",
      width: 100,
      fixed: "right",
      render: (text: any, record: any) => {
        const normalizedStatus = normalizeStatus(record.status);
        const canView = allowedActions?.view;
        const canToggleStatus = true;
        const canDelete = allowedActions?.cancel;
        const isActive = normalizedStatus === "Active";

        const availableActions = [
          canView,
          canToggleStatus,
          canDelete,
        ].filter(Boolean).length;

        return (
          <div
            className="details-cell"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "40px",
              gap: "8px",
            }}
          >
            {(canView || canToggleStatus || canDelete) && (
              <UncontrolledDropdown direction="start">
                <DropdownToggle
                  tag="button"
                  className="btn btn-sm btn-icon btn-light"
                  style={{
                    border: "none",
                    background: "none",
                    position: "relative",
                    zIndex: 1,
                    color: "#526484",
                  }}
                >
                  <Icon name="more-h" style={{ color: "inherit" }}></Icon>
                </DropdownToggle>
                <DropdownMenu
                  end
                  style={{
                    position: "fixed",
                    minWidth: "160px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    border: "1px solid #e5e5e5",
                    zIndex: 9999,
                    inset: "auto auto auto auto !important",
                  }}
                  container="body"
                >
                  <ul className="link-list-opt no-bdr" style={{ margin: 0 }}>
                    {/* view option */}
                    {canView && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#view"
                          onClick={async (ev) => {
                            ev.preventDefault();
                            // First set the record data we already have
                            setDetail({
                              gateEntryId: record.gateEntryId,
                              gateEntryNumber: record.gateEntryNumber,
                              gateEntrySequenceId: record.gateEntrySequenceId,
                              siteName: record.siteName,
                              description: record.description,
                              status: record.status,
                              createdAt: record.createdAt,
                              createdByName: record.createdByName,
                            });
                            setViewModal(true);
                            // Then fetch full details in background
                            await loadDetail(record.gateEntryId);
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Icon name="focus" className="me-2" />
                          <span>View Details</span>
                        </DropdownItem>
                      </li>
                    )}

                    {/* Status toggle - always show one of them */}
                    {canToggleStatus && (
                      <>
                        {isActive ? (
                          <li>
                            <DropdownItem
                              tag="a"
                              href="#inactivate"
                              onClick={(ev) => {
                                ev.preventDefault();
                                handleStatusChange(
                                  record.gateEntryId,
                                  "inactivate"
                                );
                              }}
                              style={{
                                padding: "8px 16px",
                                display: "flex",
                                alignItems: "center",
                                textDecoration: "none",
                              }}
                            >
                              <Icon name="cross-circle" className="me-2"></Icon>
                              <span>Inactivate</span>
                            </DropdownItem>
                          </li>
                        ) : (
                          <li>
                            <DropdownItem
                              tag="a"
                              href="#activate"
                              onClick={(ev) => {
                                ev.preventDefault();
                                handleStatusChange(
                                  record.gateEntryId,
                                  "activate"
                                );
                              }}
                              style={{
                                padding: "8px 16px",
                                display: "flex",
                                alignItems: "center",
                                textDecoration: "none",
                              }}
                            >
                              <Icon name="check-circle" className="me-2"></Icon>
                              <span>Activate</span>
                            </DropdownItem>
                          </li>
                        )}
                      </>
                    )}

                    {/* Delete option */}
                    {canDelete && (
                      <li>
                        <DropdownItem
                          tag="a"
                          href="#delete"
                          onClick={(ev) => {
                            ev.preventDefault();
                            handleDelete(record.gateEntryId);
                          }}
                          style={{
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                            textDecoration: "none",
                          }}
                        >
                          <Icon name="trash" className="me-2"></Icon>
                          <span>Delete</span>
                        </DropdownItem>
                      </li>
                    )}
                  </ul>
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="gate-entry"
        title="Gate Entry"
        columns={columns}
        selectFilters={selectFilters}
        extra={{
          tableScrollX: 1200,
          excelExport: true,
          style: { position: "relative" },
        }}
        refresh={refreshToggle}
        topRightJSX={
          allowedActions?.add ? (
            <Button
              color="primary"
              onClick={() => handleModelPopUp(null, "add")}
            >
              <Icon name="plus"></Icon>
              <span>Add</span>
            </Button>
          ) : (
            <></>
          )
        }
      />

      {/* Add/Edit Drawer */}
      <Drawer
        title={
          <div className="d-flex justify-content-between align-items-center">
            <span>
              {formAction === "edit"
                ? "Edit Gate Entry"
                : "Create New Gate Entry"}
            </span>
          </div>
        }
        placement="right"
        onClose={() => {
          setIsOpen(false);
          setFormAction(null);
        }}
        open={isOpen}
        width={900}
        styles={{ body: { paddingBottom: 80 } }}
      >
        {popUpContent}
      </Drawer>

      {/* View Details Modal */}
      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(ev) => {
              ev.preventDefault();
              setViewModal(false);
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="nk-modal-head">
            <h4 className="nk-modal-title title">
              Gate Entry Details
              {detail.gateEntryNumber && (
                <small className="text-primary ms-2">
                  - {detail.gateEntryNumber}
                </small>
              )}
            </h4>
          </div>
          <div className="nk-tnx-details mt-sm-3">
            <Row className="gy-3">
              {isSequenceSet ? (
                <>
                  {/* If sequence is set: Show Gate Entry Sequence ID and Description in same row */}
                  <Col lg={6}>
                    <span className="sub-text">Gate Entry Number</span>
                    <span className="caption-text">
                      {detail.gateEntrySequenceId || "N/A"}
                    </span>
                  </Col>
                  <Col lg={6}>
                    <span className="sub-text">Description</span>
                    <span className="caption-text">
                      {detail.description || "N/A"}
                    </span>
                  </Col>
                </>
              ) : (
                <>
                  {/* If sequence is NOT set: Show Gate Entry ID, Gate Entry Sequence ID, and Description */}
                  <Col lg={12}>
                    <span className="sub-text">Gate Entry ID</span>
                    <span className="caption-text">
                      {detail.gateEntryId || "N/A"}
                    </span>
                  </Col>
                  <Col lg={6}>
                    <span className="sub-text">Gate Entry Sequence ID</span>
                    <span className="caption-text">
                      {detail.gateEntrySequenceId || "N/A"}
                    </span>
                  </Col>
                  <Col lg={6}>
                    <span className="sub-text">Description</span>
                    <span className="caption-text">
                      {detail.description || "N/A"}
                    </span>
                  </Col>
                </>
              )}
              <Col lg={6}>
                <span className="sub-text">Site Name</span>
                <span className="caption-text">
                  {detail.siteName || "N/A"}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Created By</span>
                <span className="caption-text">
                  {detail.createdByName || "N/A"}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Created Date</span>
                <span className="caption-text">
                  {detail.createdAt ? FormatDate(detail.createdAt) : "N/A"}
                </span>
              </Col>
              <Col lg={6}>
                <span className="sub-text">Status</span>
                <Badge
                  color={
                    normalizeStatus(detail.status) === "Active"
                      ? "success"
                      : "danger"
                  }
                  size="md"
                >
                  {normalizeStatus(detail.status)}
                </Badge>
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default GateEntry;

